import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BaseURL from '../../../assets/contants/BaseURL';

const DEMO_ITEMS = [
    { name: 'GT1_EMS1', status: 'Active' },
    { name: 'GT2_EMS2', status: 'Active' },
    { name: 'GT3_EMS3', status: 'Inactive' },
    { name: 'GT4_EMS4', status: 'Warning' },
];

const getStatusColor = (status) => {
    if (status === 'Inactive') return '#f61612';
    if (status === 'Active') return '#4ded4f';
    return '#f5c518';
};

const STATUS_ICON = { Active: 'fas fa-check-circle', Inactive: 'fas fa-times-circle', Warning: 'fas fa-exclamation-circle' };
const STATUS_HEALTH = { Active: 100, Warning: 55, Inactive: 8 };

const StatusListWidget = ({ config }) => {
    const { dataSource = {}, variant = 'dot-list' } = config;
    const [remoteItems, setRemoteItems] = useState(null);

    useEffect(() => {
        if (!dataSource.endpoint) return;
        let cancelled = false;
        const token = sessionStorage.getItem('token');

        axios.get(`${BaseURL}${dataSource.endpoint}`, {
            headers: { Authorization: `Token ${token}` },
        })
            .then((res) => { if (!cancelled) setRemoteItems(res.data?.items || null); })
            .catch((err) => console.error('StatusListWidget: failed to fetch', err));

        return () => { cancelled = true; };
    }, [dataSource.endpoint]);

    const items = remoteItems || DEMO_ITEMS;

    switch (variant) {
        case 'badge-list':
            return (
                <div className="status-list-widget">
                    {items.map((item) => (
                        <div className="status-list-widget__row" key={item.name}>
                            <span className="status-list-widget__name">{item.name}</span>
                            <span
                                className="status-list-widget__badge"
                                style={{ backgroundColor: `${getStatusColor(item.status)}26`, color: getStatusColor(item.status) }}
                            >
                                {item.status}
                            </span>
                        </div>
                    ))}
                </div>
            );

        case 'icon-list':
            return (
                <div className="status-list-widget">
                    {items.map((item) => (
                        <div className="status-list-widget__row" key={item.name}>
                            <i className={STATUS_ICON[item.status] || 'fas fa-question-circle'} style={{ color: getStatusColor(item.status) }}></i>
                            <span className="status-list-widget__name">{item.name}</span>
                            <span className="status-list-widget__status">{item.status}</span>
                        </div>
                    ))}
                </div>
            );

        case 'card-grid':
            return (
                <div className="status-list-widget status-list-widget--grid">
                    {items.map((item) => (
                        <div className="status-list-widget__card" key={item.name} style={{ borderTopColor: getStatusColor(item.status) }}>
                            <span className="status-list-widget__dot" style={{ backgroundColor: getStatusColor(item.status) }} />
                            <span className="status-list-widget__name">{item.name}</span>
                            <span className="status-list-widget__status">{item.status}</span>
                        </div>
                    ))}
                </div>
            );

        case 'avatar-list':
            return (
                <div className="status-list-widget">
                    {items.map((item) => (
                        <div className="status-list-widget__row" key={item.name}>
                            <span className="status-list-widget__avatar" style={{ backgroundColor: getStatusColor(item.status) }}>
                                {item.name.charAt(0)}
                            </span>
                            <span className="status-list-widget__name">{item.name}</span>
                            <span className="status-list-widget__status">{item.status}</span>
                        </div>
                    ))}
                </div>
            );

        case 'progress-rows':
            return (
                <div className="status-list-widget">
                    {items.map((item) => (
                        <div className="status-list-widget__row status-list-widget__row--health" key={item.name}>
                            <span className="status-list-widget__name">{item.name}</span>
                            <div className="status-list-widget__health-track">
                                <div
                                    className="status-list-widget__health-fill"
                                    style={{ width: `${STATUS_HEALTH[item.status] ?? 50}%`, backgroundColor: getStatusColor(item.status) }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            );

        case 'timeline':
            return (
                <div className="status-list-widget status-list-widget--timeline">
                    {items.map((item) => (
                        <div className="status-list-widget__timeline-row" key={item.name}>
                            <span className="status-list-widget__timeline-dot" style={{ backgroundColor: getStatusColor(item.status) }} />
                            <div className="status-list-widget__timeline-body">
                                <span className="status-list-widget__name">{item.name}</span>
                                <span className="status-list-widget__status">{item.status}</span>
                            </div>
                        </div>
                    ))}
                </div>
            );

        case 'table-style':
            return (
                <div className="status-list-widget">
                    <table className="status-list-widget__table">
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.name}>
                                    <td>{item.name}</td>
                                    <td style={{ color: getStatusColor(item.status) }}>{item.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );

        case 'compact-chips':
            return (
                <div className="status-list-widget status-list-widget--chips">
                    {items.map((item) => (
                        <span
                            className="status-list-widget__chip"
                            key={item.name}
                            style={{ borderColor: getStatusColor(item.status), color: getStatusColor(item.status) }}
                        >
                            {item.name}
                        </span>
                    ))}
                </div>
            );

        case 'minimal-text':
            return (
                <div className="status-list-widget">
                    {items.map((item) => (
                        <div className="status-list-widget__row status-list-widget__row--minimal" key={item.name}>
                            <span style={{ color: getStatusColor(item.status), fontWeight: 600 }}>{item.name}</span>
                            <span className="status-list-widget__status">{item.status}</span>
                        </div>
                    ))}
                </div>
            );

        case 'dot-list':
        default:
            return (
                <div className="status-list-widget">
                    {items.map((item) => (
                        <div className="status-list-widget__row" key={item.name}>
                            <span className="status-list-widget__dot" style={{ backgroundColor: getStatusColor(item.status) }} />
                            <span className="status-list-widget__name">{item.name}</span>
                            <span className="status-list-widget__status">{item.status}</span>
                        </div>
                    ))}
                </div>
            );
    }
};

export default StatusListWidget;
